import React, { FC } from 'react';
import classNames from 'classnames';
import { BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import styles from './GenBanner.module.scss';
import { GenExternalLink } from '@/components';
import { GenExternalLinkProps } from '@/components/generics/GenExternalLink/GenExternalLink';
import Heading, {
  HeadingProps,
  HeadingSize,
} from '@/components/ui/Heading/Heading';

export enum BannerColorScheme {
  SUCCESS = 'success',
  NOTICE = 'notice',
  ERROR = 'error',
  HOLIDAY = 'holiday',
  UPSELL = 'upsell',
}

export enum BannerBorderType {
  NONE = 'none',
  SOLID = 'solid',
  DASHED = 'dashed',
}

export enum BannerParagraphAlignment {
  LEFT = 'text-left',
  CENTER = 'text-center',
}

const getBannerStyles = (
  borderType: BannerBorderType,
  colorScheme: BannerColorScheme,
) => {
  const borderStyle =
    borderType === BannerBorderType.DASHED ? styles[`${colorScheme}Dash`] : '';

  const baseStyles = {
    success: 'bg-kelly-green-tint border-success fill-success',
    notice: 'bg-yellow-tint border-yellow',
    error: 'bg-red-tint border-warning',
    holiday: 'bg-yellow-pastel border-red-v2',
    upsell: 'bg-indigo-pastel border-blue-deep',
  };

  return `${baseStyles[colorScheme]} ${borderStyle}`;
};

export const ICON_STYLES = {
  success: '#19883E',
  notice: '#424140',
  error: '#D52300',
  holiday: '#360019',
  upsell: '#424140',
};

const HEADING_STYLES = {
  success: '!text-success',
  notice: '',
  error: '!text-warning',
  holiday: '!text-maroon-dark',
  upsell: '',
};

const PARAGRAPH_STYLES = {
  success: '',
  notice: '',
  error: 'text-warning',
  holiday: 'text-maroon-dark',
  upsell: '',
};

export const ICON_DESKTOP_SIZE_MAPPING: Record<HeadingSize, number> = {
  [HeadingSize.HERO]: 64,
  [HeadingSize.H1]: 42,
  [HeadingSize.H2]: 32,
  [HeadingSize.H3]: 24,
  [HeadingSize.H4]: 18,
};

export const ICON_MOBILE_SIZE_MAPPING: Record<HeadingSize, number> = {
  [HeadingSize.HERO]: 64,
  [HeadingSize.H1]: 32,
  [HeadingSize.H2]: 24,
  [HeadingSize.H3]: 20,
  [HeadingSize.H4]: 18,
};

export interface GenBannerProps {
  colorScheme?: BannerColorScheme;
  borderType?: BannerBorderType;
  ctaAttr?: GenExternalLinkProps;
  headingAttr?: HeadingProps;
  icon?: React.ReactNode;
  image?: React.ReactNode;
  paragraphText?: React.ReactNode;
  paragraphAlignment?: BannerParagraphAlignment;
  testId?: string;
  classes?: string;
  noWrapCta?: boolean;
}

const GenBanner: FC<GenBannerProps> = ({
  ctaAttr,
  headingAttr,
  icon,
  image,
  paragraphText,
  colorScheme = BannerColorScheme.UPSELL,
  borderType = BannerBorderType.NONE,
  paragraphAlignment = BannerParagraphAlignment.LEFT,
  testId = 'banner',
  classes = '',
  noWrapCta = false,
}) => {
  return (
    <div
      data-testid={testId}
      className={classNames(
        'w-full flex flex-col md:flex-row  p-4 md:p-6 border rounded-md',
        {
          'md:items-center': noWrapCta,
          'content-center justify-center items-center': !noWrapCta,
        },
        getBannerStyles(borderType, colorScheme),
        classes,
      )}
    >
      {image && <div className="self-center md:pr-6 md:pb-0 pb-4">{image}</div>}

      <div
        className={classNames('flex flex-col md:flex-row', {
          'md:items-center md:gap-6': noWrapCta,
          'gap-2 md:gap-4 items-center': !noWrapCta,
        })}
      >
        <div
          className={classNames('items-center flex flex-row', {
            'md:max-w-[497px]': noWrapCta,
            'md:pr-6': !noWrapCta,
          })}
        >
          {icon && (
            <div
              className={classNames({
                ['self-start']: !!paragraphText,
              })}
            >
              {icon}
            </div>
          )}
          <div className="md:pl-4">
            {headingAttr && (
              <Heading
                classes={`${HEADING_STYLES[colorScheme]} !leading-none`}
                {...headingAttr}
              />
            )}
            {paragraphText && (
              <p
                className={`text-sm md:text-base ${PARAGRAPH_STYLES[colorScheme]} ${paragraphAlignment}`}
              >
                {paragraphText}
              </p>
            )}
          </div>
        </div>

        {ctaAttr && (
          <GenExternalLink
            {...ctaAttr}
            classes={classNames('mt-4 md:mt-0', {
              'md:ml-6 md:whitespace-nowrap':
                noWrapCta && ctaAttr.style === BUTTON_STYLES.link,
              'w-full md:w-auto md:ml-6 md:whitespace-nowrap':
                noWrapCta && ctaAttr.style === BUTTON_STYLES.primary,
              'w-full md:w-fit':
                !noWrapCta && ctaAttr.style === BUTTON_STYLES.primary,
            })}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(GenBanner);
